import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useLazyInitReservationListQuery } from "../../../store/services/ReservationService";
import { ReservationListInterface } from "../../../store/slices/reservationSlice";
import { useTypedSelector } from "../../../store/store";
import { SEARCH } from "../../../utils/constants/routes";
import LoadingContainer from "../../containers/LoadingContainer";
import Alert from "../../shared/Alert";
import DatePicker, { DatePickerHandler } from "../../shared/DatePicker";
import Input from "../../shared/Input";

import styles from "./index.module.scss";
import Content from "./molecules/Content";

dayjs.extend(customParseFormat);

type SortType = "passenger_name" | "embarkation_date" | "booking_date";

interface InputInterface {
  value: string[];
  errorMessage?: string;
}

interface SearchInputsInterface {
  [key: string]: InputInterface;
  bookingName: InputInterface;
  embarkDate: InputInterface;
  yachtName: InputInterface;
}

function CurrentBookings() {
  const [getReservations] = useLazyInitReservationListQuery();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    current_page_title,
    yacht_name_placeholder,
    current_booking_payment_info,
  } = useTypedSelector((state) => state.environment);

  const [isReservationLoading, setReservationLoading] = useState(true);

  const session = useTypedSelector((state) => state.session);

  const bookingList = useTypedSelector(
    (state) => state.reservation.reservationList,
  );

  const [bookingListState, setBookingListState] = useState<
    ReservationListInterface[]
  >([]);

  const [searchInputs, setSearchInputs] = useState<SearchInputsInterface>({
    bookingName: { value: [""] },
    yachtName: { value: [""] },
    embarkDate: {
      value: [],
      errorMessage: "",
    },
  });

  const datePickerRef = useRef<DatePickerHandler>(null);

  const inMonthRange = (
    embarkationDate: string,
    startMonthSelection?: string,
    endMonthSelection?: string,
  ) => {
    if (!startMonthSelection || !endMonthSelection) {
      return false;
    }

    const parseMonthLikeDate = (value: unknown) => {
      let parsed = dayjs(
        value as any,
        ["YYYY-MM", "YYYY-MM-DD", "MMMM YYYY"],
        true,
      );

      if (!parsed.isValid()) {
        parsed = dayjs(value as any);
      }

      return parsed.isValid() ? parsed : null;
    };

    const startParsed = parseMonthLikeDate(startMonthSelection);
    const endParsed = parseMonthLikeDate(endMonthSelection);

    if (!startParsed || !endParsed) {
      return false;
    }

    const startBoundary = startParsed.startOf("month");
    const endBoundary = endParsed.endOf("month");

    let embarkationParsed = dayjs(
      embarkationDate,
      ["YYYY-MM-DD", "YYYY-MM"],
      true,
    );

    if (!embarkationParsed.isValid()) {
      embarkationParsed = dayjs(embarkationDate);
    }

    if (!embarkationParsed.isValid()) {
      return false;
    }

    const embarkationMs = embarkationParsed.valueOf();

    return (
      embarkationMs >= startBoundary.valueOf() &&
      embarkationMs <= endBoundary.valueOf()
    );
  };

  const [filterBookingList, setFilterBookingList] = useState<
    ReservationListInterface[]
  >([]);

  const isUsingFilter = useMemo(() => {
    for (const key in searchInputs) {
      const value = searchInputs[key].value;

      if (value.length !== 0 && value[0] !== "") {
        return true;
      }
    }

    return false;
  }, [searchInputs]);

  const [sortOn, setSortOn] = useState({ type: "", first: false });

  const initReservations = () => {
    getReservations({
      channel: "CS",
      channel_partner: session.agency,
      agency: session.agent,
      embarkation_from: dayjs().format("YYYY-MM-DD"),
    })
      .then((res) => {
        if (!res.data) {
          return;
        }

        setBookingListState(res.data);
      })
      .finally(() => {
        setReservationLoading(false);
      });
  };

  const sortBookingList = (type: SortType) => {
    const nextFirst = sortOn.type === type ? !sortOn.first : false;
    const dir = nextFirst ? -1 : 1;

    const comparator = (
      a: ReservationListInterface,
      b: ReservationListInterface,
    ) => {
      if (type === "passenger_name") {
        return (
          dir * (a.lead_lastname || "").localeCompare(b.lead_lastname || "")
        );
      }

      if (type === "embarkation_date") {
        const da = dayjs(a.embarkation_date, "YYYY-MM-DD");
        const db = dayjs(b.embarkation_date, "YYYY-MM-DD");

        return dir * (da.valueOf() - db.valueOf());
      }

      const da = dayjs(a.created, "YYYY-MM-DD HH:mm");
      const db = dayjs(b.created, "YYYY-MM-DD HH:mm");

      return dir * (da.valueOf() - db.valueOf());
    };

    setFilterBookingList((prev) => [...prev].sort(comparator));
    setBookingListState((prev) => [...prev].sort(comparator));
    setSortOn({ type, first: nextFirst });
  };

  const handleInputChange = ({
    value,
    valueKey,
  }: {
    value: string | string[];
    valueKey?: string;
  }) => {
    if (valueKey) {
      setSearchInputs((prev: SearchInputsInterface) => {
        return {
          ...prev,
          [valueKey]: {
            errorMessage: "",
            value: Array.isArray(value) ? value : [value],
          },
        };
      });
    }
  };

  const resetFiltersHandler = () => {
    setSearchInputs({
      bookingName: { value: [""] },
      embarkDate: {
        value: [],
        errorMessage: "",
      },
      yachtName: { value: [""] },
      expeditionType: { value: [] },
    });

    datePickerRef.current?.handleResetDate();
  };

  useEffect(() => {
    const filterTotal = Array.isArray(bookingList)
      ? bookingList.filter((item) => {
          const checkBookingName = searchInputs.bookingName.value[0]
            ? (item.lead_lastname ?? "")
                .toLowerCase()
                .includes(searchInputs.bookingName.value[0].toLowerCase())
            : false;

          const checkYachName = searchInputs.yachtName.value[0]
            ? item.ship_name
                .toLowerCase()
                .includes(searchInputs.yachtName.value[0].toLowerCase())
            : false;

          const checkDate = inMonthRange(
            item.embarkation_date,
            searchInputs.embarkDate.value[0],
            searchInputs.embarkDate.value[1],
          );

          return checkBookingName || checkYachName || checkDate;
        })
      : [];

    setFilterBookingList(filterTotal);
  }, [searchInputs]);

  useEffect(initReservations, []);

  return (
    <LoadingContainer isLoading={isReservationLoading}>
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.filtersHeader}>
            <h1>{current_page_title}</h1>

            <button
              className={styles.startBooking}
              onClick={() => navigate(SEARCH)}>
              {t("START A BOOKING")}
            </button>
          </div>

          {Boolean(current_booking_payment_info) && (
            <Alert message={current_booking_payment_info} />
          )}

          <div className={styles.filtersHeader}>
            <span>{t("Click on a booking to find out more information.")}</span>
          </div>

          <div className={styles.filtersHeader}>
            <span>{t("FILTER BOOKINGS BY")}</span>

            <button
              className={styles.resetFilters}
              onClick={resetFiltersHandler}>
              {t("RESET FILTERS")}
            </button>
          </div>

          <form className={styles.form}>
            <Input
              value={searchInputs.bookingName.value[0]}
              valueKey="bookingName"
              placeholder={t("by name")}
              errorMessage={searchInputs.bookingName.errorMessage}
              onChange={handleInputChange}
              className={styles.input}
            />

            <DatePicker
              valueKey="embarkDate"
              placeholder={t("embark date")}
              returnFormat="YYYY-MM"
              defaultDate={searchInputs.embarkDate.value}
              errorMessage={searchInputs.embarkDate.errorMessage}
              onChange={handleInputChange}
              ref={datePickerRef}
              className={styles.inputDate}
              mode="range"
              minDate={dayjs(dayjs().subtract(1, "month"))}
            />

            <Input
              value={searchInputs.yachtName.value[0]}
              valueKey="yachtName"
              placeholder={yacht_name_placeholder}
              errorMessage={searchInputs.yachtName.errorMessage}
              onChange={handleInputChange}
              className={styles.input}
            />
          </form>

          <Content
            bookingList={isUsingFilter ? filterBookingList : bookingListState}
            sortBookingList={sortBookingList}
            onModalClose={initReservations}
          />
        </div>
      </div>
    </LoadingContainer>
  );
}

export default CurrentBookings;
